<template>
    <div v-if="selectedBlockchainTxn">

        <Drawer
            :drawerStatus="traceViewDrawerStatus"
            bgColor='#90CAF9'
            headerBgColor="#90CAF9"
            drawerWidth='53%'
            zIndex=300
            :title="selectedBlockchainTxn.chaincode_name + '::' + selectedBlockchainTxn.chaincode_function"
            :subtitle="toLongFormat(selectedBlockchainTxn.start_time)"
            v-on:onDismiss="traceViewDrawerStatus = 'collapsed'"
            v-on:onOverlayClicked="$emit('onDismissView'); traceViewDrawerStatus = 'collapsed'">
            <div class="px-5">
                <v-row>
                    <v-col>
                        <h3 class="text-uppercase underlined-title dark">Invocation Details</h3>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col class="pt-0 pb-3" cols="4">
                        <h5 class="font-weight-light">Channel name</h5>
                        <span>{{ selectedBlockchainTxn.channel_name }}</span>
                    </v-col>
                    <v-col class="pt-0 pb-3" cols="4">
                        <h5 class="font-weight-light">Chaincode name</h5>
                        <span>{{ selectedBlockchainTxn.chaincode_name }}</span>
                    </v-col>
                    <v-col class="pt-0 pb-3" cols="4">
                        <h5 class="font-weight-light">Chaincode function name</h5>
                        <span>{{ selectedBlockchainTxn.chaincode_function }}</span>
                    </v-col>
                </v-row>            
                <v-row>
                    <v-col class="pt-0 pb-3" cols="4">
                        <h5 class="font-weight-light">Completion Time</h5>
                        <span class="">{{ standardFormat(selectedBlockchainTxn.completion_time) }}ms</span>
                    </v-col>
                    <v-col class="pt-0 pb-3" cols="4">
                        <h5 class="font-weight-light">Endorsers</h5>
                        <span class="">{{ endorsers.length }}</span>
                    </v-col>
                    <v-col class="pt-0 pb-3" cols="4">
                        <h5 class="font-weight-light">Orderers</h5>
                        <span class="">{{ orderers.length }}</span>
                    </v-col>
                </v-row>            
                <v-row>
                    <v-col class="pt-0 pb-3" cols="6">
                        <h5 class="font-weight-light">Parameters</h5>
                        <div v-if="selectedBlockchainTxn.parameters">
                            <template v-for="(param, i) in JSON.parse(selectedBlockchainTxn.parameters)" >
                                    <VueReadMoreSmooth v-bind:key="i">
                                        <template v-if="isJSON(param)"><vue-json-pretty :path="'res'" :data="JSON.parse(param)"></vue-json-pretty></template>
                                        <template v-else><span class="monospace">{{ param }}</span></template>
                                    </VueReadMoreSmooth>
                            </template>
                        </div>         
                    </v-col>
                   <v-col class="pt-0 pb-3" cols="6">
                        <h5 class="font-weight-light">Transient Map</h5>
                        <div v-if="selectedBlockchainTxn.transient_map">
                            <VueReadMoreSmooth>
                                <vue-json-pretty :path="'res'" :data="JSON.parse(selectedBlockchainTxn.transient_map)"></vue-json-pretty>
                            </VueReadMoreSmooth>
                        </div>                                    
                    </v-col>
                </v-row>                            
                <v-row>
                    <v-col>
                        <h3 class="text-uppercase underlined-title dark">Trace</h3>
                    </v-col>
                </v-row>
                <v-row no-gutters class="pb-4 font-weight-bold text-body-2">
                    <v-col cols="1"><span>Level</span></v-col>
                    <v-col cols="3"><span>Module & Function</span></v-col>
                    <v-col cols="1"><span>Event</span></v-col>
                    <v-col cols="3"><span>Parameters / Returned Data</span></v-col>
                    <v-col class="text-center" cols="2"><span>Time</span></v-col>
                    <v-col class="text-center" cols="2"><span>Time Taken (ms)</span></v-col>
                </v-row>

                <template v-for="(traceRecord, index) in traceRecords" >
                    <v-row no-gutters v-if="traceRecord.module_name == highlightTraceModuleName && traceRecord.function_name == highlightTraceFunctionName"
                        v-breathing-colors="breathingColorOptions"
                        v-bind:key="traceRecord.id"
                        class="px-4 py-2">
                        <v-col class="trace-event-cell" cols="1"><span>{{ traceRecord.nest_level }}</span></v-col>
                        <v-col v-if="traceRecord.module_name == 'FABRIC LEDGER'" class="trace-event-cell" cols="3"><span>{{ traceRecord.collection + "::" + traceRecord.function_name }}</span></v-col>
                        <v-col v-else class="trace-event-cell" cols="3"><span>{{ traceRecord.module_name + "::" + traceRecord.function_name }}</span></v-col>
                        <v-col class="trace-event-cell" cols="1"><span>{{ traceRecord.event_type == 1 ? 'Entry' : 'Exit' }}</span></v-col>
                        <v-col class="trace-event-cell" cols="3"><span>{{ traceRecord.event_type == 1 ? traceRecord.args : traceRecord.returned_result }}</span></v-col>
                        <v-col class="trace-event-cell text-center" cols="2"><span>{{ toShortFormat( traceRecord.event_time ) }}</span></v-col>
                        <v-col class="trace-event-cell text-center" cols="2"><span>{{ traceRecord.event_type == 2 ? traceRecord.time_taken : '--' }}</span></v-col>
                    </v-row>                
                    <v-row no-gutters v-else
                        v-bind:key="traceRecord.id"
                        class="px-4 py-2"
                        :class="(highlightTraceModuleName != null) || (index % 2 != 0 ) ? 'trace-event-row-odd' : 'trace-event-row-even'">
                        <v-col class="trace-event-cell" cols="1"><span>{{ traceRecord.nest_level }}</span></v-col>
                        <v-col v-if="traceRecord.module_name == 'FABRIC LEDGER'" class="trace-event-cell" cols="3"><span>{{ traceRecord.collection + "::" + traceRecord.function_name }}</span></v-col>
                        <v-col v-else class="trace-event-cell" cols="3"><span>{{ traceRecord.module_name + "::" + traceRecord.function_name }}</span></v-col>
                        <v-col class="trace-event-cell" cols="1"><span>{{ traceRecord.event_type == 1 ? 'Entry' : 'Exit' }}</span></v-col>
                        <v-col class="trace-event-cell" cols="3">
                            <span v-if="traceRecord.event_type == 1">
                                <div>{{ traceRecord.args }}</div>
                                <div>{{ traceRecord.transient_map }}</div>
                            </span>
                            <span v-else>{{ traceRecord.returned_result }}</span>
                        </v-col>
                        <v-col class="trace-event-cell text-center" cols="2"><span>{{ toShortFormat( traceRecord.event_time ) }}</span></v-col>
                        <v-col class="trace-event-cell text-center" cols="2"><span>{{ traceRecord.event_type == 2 ? traceRecord.time_taken : '--' }}</span></v-col>
                    </v-row>                                
                </template>            

            </div>
        </Drawer>

        <div>
            <v-row no-gutters>
                <v-col cols="11">
                    <v-tabs @change="magicRender" v-model="tab" centered class="ml-10" background-color="transparent">
                        <v-tab>Invocation Results</v-tab>
                        <v-tab>Chaincode Performance</v-tab>
                    </v-tabs>                    
                </v-col>
            </v-row>
            <v-row>
                <v-col>
                    <v-tabs-items class="blockchain-invocation-detail-tabs" v-model="tab">
                        <!-- FIRST TAB -->
                        <v-tab-item>
                            <v-row>
                                <v-col>
                                    <h3 class="text-uppercase underlined-title dark">
                                        Invocation Status
                                        <v-icon class="ml-2 mb-1" v-if="selectedBlockchainTxn.status" color="green">mdi-check-circle</v-icon>
                                        <v-icon v-else color="red">mdi-alert-circle</v-icon>
                                    </h3>
                                </v-col>
                            </v-row>
                            <v-row no-gutters>
                                <v-col>
                                    <div v-if="!selectedBlockchainTxn.status" style="white-space: pre-line" class="font-weight-light text-body-2 mt-3">{{ selectedBlockchainTxn.error_stack }}</div>
                                    <div v-else>Success</div>                                    
                                </v-col>                                
                            </v-row>
                            <v-row>
                                <v-col>
                                    <h3 class="text-uppercase underlined-title dark">Invocation Details</h3>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="pt-0 pb-3" cols="4">
                                    <h5 class="font-weight-light">Channel name</h5>
                                    <span>{{ selectedBlockchainTxn.channel_name }}</span>
                                </v-col>
                                <v-col class="pt-0 pb-3" cols="4">
                                    <h5 class="font-weight-light">Chaincode name</h5>
                                    <span>{{ selectedBlockchainTxn.chaincode_name }}</span>
                                </v-col>
                                <v-col class="pt-0 pb-3" cols="4">
                                    <h5 class="font-weight-light">Chaincode function name</h5>
                                    <span>{{ selectedBlockchainTxn.chaincode_function }}</span>
                                </v-col>
                            </v-row>            
                            <v-row>
                                <v-col class="pt-0 pb-3" cols="4">
                                    <h5 class="font-weight-light">Completion Time</h5>
                                    <span class="">{{ standardFormat(selectedBlockchainTxn.completion_time) }}ms</span>
                                </v-col>
                                <v-col class="pt-0 pb-3" cols="4">
                                    <h5 class="font-weight-light">Endorsers</h5>
                                    <span class="">{{ endorsers.length }}</span>
                                </v-col>
                                <v-col class="pt-0 pb-3" cols="4">
                                    <h5 class="font-weight-light">Orderers</h5>
                                    <span class="">{{ orderers.length }}</span>
                                </v-col>
                            </v-row>            
                            <v-row>
                                <v-col class="pt-0 pb-3" cols="6">
                                    <h5 class="font-weight-light">Parameters</h5>
                                    <div v-if="selectedBlockchainTxn.parameters">
                                        <template v-for="(param, i) in JSON.parse(selectedBlockchainTxn.parameters)" >
                                                <VueReadMoreSmooth v-bind:key="i">
                                                    <template v-if="isJSON(param)"><vue-json-pretty :path="'res'" :data="JSON.parse(param)"></vue-json-pretty></template>
                                                    <template v-else><span class="monospace">{{ param }}</span></template>
                                                </VueReadMoreSmooth>
                                        </template>
                                    </div>                                    
                                </v-col>
                                <v-col class="pt-0 pb-3" cols="6">
                                        <h5 class="font-weight-light">Transient Map</h5>
                                        <div v-if="selectedBlockchainTxn.transient_map">
                                            <VueReadMoreSmooth>
                                                <vue-json-pretty :path="'res'" :data="JSON.parse(selectedBlockchainTxn.transient_map)"></vue-json-pretty>
                                            </VueReadMoreSmooth>
                                        </div>                                    
                                </v-col>
                            </v-row>             
                            <template v-if="topologyDataReady">
                                <v-row>
                                    <v-col>
                                        <h3 class="text-uppercase underlined-title dark">
                                            Endorsement Status
                                            <template>
                                                <v-icon class="ml-2 mb-1" v-if="endorsementStatus" color="green">mdi-check-circle</v-icon>
                                                <v-icon class="ml-2 mb-1" v-else color="red">mdi-alert-circle</v-icon>
                                            </template>
                                        </h3>
                                    </v-col>
                                </v-row>
                                <v-row no-gutters>
                                    <v-col cols="4">
                                        <!-- NODE INFORMATION START -->
                                        <v-expand-transition>
                                            <v-card v-if="selectedNode" color="#385F73" dark>
                                                <v-card-title>
                                                    <v-icon color="green" class="mr-2" v-if="selectedNode.acceptance || selectedNode.success">mdi-check-decagram</v-icon>
                                                    <v-icon color="yellow" class="mr-2" v-if="selectedNode.hasError && selectedNode.isWarning">mdi-alert-decagram</v-icon>
                                                    <v-icon color="red" class="mr-2" v-if="selectedNode.hasError && !selectedNode.isWarning">mdi-alert-decagram</v-icon>
                                                    {{ selectedNode.role }}
                                                </v-card-title>
                                                <v-card-subtitle>{{ selectedNode.msp_id }}</v-card-subtitle>
                                                <v-card-text>{{ selectedNode.name }}</v-card-text>
                                                <template v-if="selectedNode.acceptance">
                                                    <v-card-subtitle>Endorsed at:&nbsp;<span>{{ toHHMMSSFormat(selectedNode.accepted_at) }}</span></v-card-subtitle>
                                                </template>
                                                <template v-if="selectedNode.hasError">
                                                    <v-card-subtitle
                                                        :class="selectedNode.isWarning ? 'yellow--text yellow--lighten-4' : 'red--text text--lighten-4'"
                                                        class="text-uppercase">
                                                        {{ selectedNode.isWarning ? 'warning' : 'error' }}
                                                    </v-card-subtitle>
                                                    <v-card-text
                                                        :class="selectedNode.isWarning ? 'yellow--text yellow--lighten-4' : 'red--text text--lighten-4'">
                                                        {{ selectedNode.errorCode }}{{ selectedNode.errorMessage }}
                                                    </v-card-text>
                                                </template>
                                            </v-card>
                                        </v-expand-transition>
                                        <!-- NODE INFORMATION END -->
                                    </v-col>
                                    <v-col cols="8">
                                        <!-- TOPOLOGY GRAPH START -->
                                        <!-- <TopologyLegend/> -->
                                        <v-row no-gutters>
                                            <v-col>
                                                <d3-network :key="tabNumber" 
                                                    @mouse-over="handleNodeMouseOver"
                                                    @mouse-leave="handleNodeMouseLeave"
                                                    :net-nodes="generateNodes"
                                                    :net-links="generateLinks"
                                                    :options="nodeOptions" />
                                                </v-col>
                                        </v-row>
                                        <!-- TOPOLOGY GRAPH END -->
                                    </v-col>
                                </v-row>
                            </template>               
                        </v-tab-item>
                        <!-- END OF FIRST TAB -->
                        <!-- SECOND TAB -->
                        <v-tab-item>
                            <v-row>
                                <v-col>
                                    <h3 class="text-uppercase underlined-title dark">Invocation Details</h3>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="pt-0 pb-3" cols="4">
                                    <h5 class="font-weight-light">Channel name</h5>
                                    <span>{{ selectedBlockchainTxn.channel_name }}</span>
                                </v-col>
                                <v-col class="pt-0 pb-3" cols="4">
                                    <h5 class="font-weight-light">Chaincode name</h5>
                                    <span>{{ selectedBlockchainTxn.chaincode_name }}</span>
                                </v-col>
                                <v-col class="pt-0 pb-3" cols="4">
                                    <h5 class="font-weight-light">Chaincode function name</h5>
                                    <span>{{ selectedBlockchainTxn.chaincode_function }}</span>
                                </v-col>
                            </v-row>            
                            <v-row>
                                <v-col class="pt-0 pb-3" cols="4">
                                    <h5 class="font-weight-light">Completion Time</h5>
                                    <span class="">{{ standardFormat(selectedBlockchainTxn.completion_time) }}ms</span>
                                </v-col>
                                <v-col class="pt-0 pb-3" cols="4">
                                    <h5 class="font-weight-light">Endorsers</h5>
                                    <span class="">{{ endorsers.length }}</span>
                                </v-col>
                                <v-col class="pt-0 pb-3" cols="4">
                                    <h5 class="font-weight-light">Orderers</h5>
                                    <span class="">{{ orderers.length }}</span>
                                </v-col>
                            </v-row>            
                            <v-row>
                                <v-col class="pt-0 pb-3" cols="6">
                                    <h5 class="font-weight-light">Parameters</h5>
                                    <div v-if="selectedBlockchainTxn.parameters">
                                        <template v-for="(param, i) in JSON.parse(selectedBlockchainTxn.parameters)" >
                                                <VueReadMoreSmooth v-bind:key="i">
                                                    <template v-if="isJSON(param)"><vue-json-pretty :path="'res'" :data="JSON.parse(param)"></vue-json-pretty></template>
                                                    <template v-else><span class="monospace">{{ param }}</span></template>
                                                </VueReadMoreSmooth>
                                        </template>
                                    </div>         
                                </v-col>
                                <v-col class="pt-0 pb-3" cols="6">
                                    <h5 class="font-weight-light">Transient Map</h5>
                                    <div v-if="selectedBlockchainTxn.transient_map">
                                        <VueReadMoreSmooth>
                                            <vue-json-pretty :path="'res'" :data="JSON.parse(selectedBlockchainTxn.transient_map)"></vue-json-pretty>
                                        </VueReadMoreSmooth>
                                    </div>                                    
                                </v-col>
                            </v-row>           
                            <v-row>
                                <v-col>
                                    <h3 class="text-uppercase underlined-title dark">Invocation Stages</h3>
                                </v-col>
                            </v-row>
                            <v-row no-gutters>
                                <v-col class="text-center">
                                    <v-icon class="ml-5 mb-1" size="24" color="brown lighten-2">mdi-square</v-icon><span class="text-body-2">PROPOSE</span>
                                    <v-icon class="ml-5 mb-1" size="24" color="blue-grey lighten-2">mdi-square</v-icon><span class="text-body-2">ENDORSE</span>
                                    <v-icon class="ml-5 mb-1" size="24" color="indigo lighten-2">mdi-square</v-icon><span class="text-body-2">ORDER</span>
                                    <v-icon class="ml-5 mb-1" size="24" color="purple lighten-2">mdi-square</v-icon><span class="text-body-2">COMMIT</span>
                                </v-col>
                            </v-row>
                            <v-row no-gutters>
                                <v-col>
                                    <apexchart :key="tabNumber" v-if="bcTransactionFlowChartSeries[0].data.length > 0" height="300" :options="bcTransactionFlowChartOptions" :series="bcTransactionFlowChartSeries"></apexchart>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col>
                                    <h3 class="text-uppercase underlined-title dark">Chaincode Method Performance Breakdown<v-btn text small color="primary" v-on:click.stop="handleFullTraceClick">Full Trace</v-btn></h3>
                                </v-col>
                            </v-row>
                            <v-row no-gutters>
                                <v-col>
                                    <apexchart :key="tabNumber" type="bar" height="200" :options="slowestFunctionsOptions()" :series="slowestFunctionsSeries"></apexchart>
                                </v-col>
                            </v-row>
                            </v-tab-item>
                        <!-- END OF SECOND TAB -->
                    </v-tabs-items>                
                </v-col>
            </v-row>
        </div>

    </div>
</template>

<script>
    import D3Network from 'vue-d3-network'
    import VueJsonPretty from 'vue-json-pretty'
    import VueReadMoreSmooth from "vue-read-more-smooth"

    import TopologyLegend from '@/components/TopologyLegend.vue'
    import Drawer from '@/components/Drawer.vue'

    import Api from '@/services/api'
    import TimeHelper from '@/helpers/time_helper'
    import NumberHelper from '@/helpers/number_helper'
    import ColorHelper from '@/helpers/color_helper'
    import StringHelper from '@/helpers/string_helper'
    import TopologyHelper from '@/helpers/topology_helper'
    import Constants from '@/config/constants'

    export default {

        components: {
            D3Network, VueJsonPretty, VueReadMoreSmooth, Drawer
        },

        name: 'BlockchainRequestView',

        props: {
            selectedBlockchainTxn: Object,
        },  
        
        mounted: function() {
            if (this.selectedBlockchainTxn) {
                this.drawTopology();
                this.getInvocationStageData();
                this.getSlowestFunctions();                
            }
        },

        computed: {
            generateNodes: function() { return TopologyHelper.generateNodes(this.endorsers, this.orderers, 'large', true); },
            generateLinks: function() { return TopologyHelper.generateLinks(this.endorsers, this.orderers, 'large', true); }
        },

        watch: {
            selectedBlockchainTxn: function (newVal, oldVal) {
                if (this.selectedBlockchainTxn) {
                    this.drawTopology();
                    this.getInvocationStageData();
                    this.getSlowestFunctions();                
                }
            },
        },

        methods: {

            ...NumberHelper,

            ...StringHelper,

            ...TimeHelper,

            drawTopology: function() {
                this.topologyDataReady = false 
                this.endorsers = []
                this.orderers  = []
                this.endorsementErrorSteps = []
                Api.blockchainRequests.getTopologyByRavenpodTxnId({},{ravenpod_txn_id: this.selectedBlockchainTxn.ravenpod_txn_id})
                .then((response1) => {
                    console.log("Topology data for the selected blockchain txn", response1.data)
                    this.endorsers = response1.data.endorsers
                    this.orderers = response1.data.orderers
                    this.getEndorsementResults();
                })
            },

            getEndorsementResults: function() {
                Api.blockchainRequests.getEndorsementResults({},{ravenpod_txn_id: this.selectedBlockchainTxn.ravenpod_txn_id})
                .then((response) => {
                    console.log("Endorsement Results for the selected blockchain txn", response.data)
                    const endorsementResult = response.data
                    if (endorsementResult.status == 'success') {
                        this.endorsementStatus = true
                        // Set endorsers which did endorsement acceptance to green
                        for (let index1 = 0; index1 < endorsementResult.endorsed_by.length; index1++) {
                            const endorsementAcceptance = endorsementResult.endorsed_by[index1];
                            const hostname = endorsementAcceptance.hostname
                            for (let index2 = 0; index2 < this.endorsers.length; index2++ ) {
                                let endorser = this.endorsers[index2]
                                if (endorser.name == hostname) {
                                    endorser.acceptance = true
                                    endorser.accepted_at = endorsementAcceptance.created_at
                                    this.$set(this.endorsers, index2, endorser)
                                    break;                                
                                }
                            }
                        }
                        // Orderer should be in green 
                        // for (let index = 0; index < this.orderers.length; index++ ) {
                        //     let orderer = this.orderers[index]
                        //     orderer.success = true
                        //     this.$set(this.orderers, index, orderer)
                        // }                                                
                    } else {
                        this.endorsementStatus = false 
                    }

                    // Add error peers
                    let errorPeers = [] // This is to ensure no dup of endorser will be created
                    for (let index = 0; index < endorsementResult.errors.length; index++) {
                        const endorsementErrorStep = endorsementResult.errors[index]                        
                        if (!errorPeers.includes(endorsementErrorStep.hostname)) {
                            const errorPeer = {
                                id: endorsementErrorStep.id,
                                msp_id: endorsementErrorStep.msp_id,
                                name: endorsementErrorStep.hostname,
                                url: endorsementErrorStep.endpoint,
                                hasError: true,
                                isWarning: this.endorsementStatus,
                                errorCode: endorsementErrorStep.error_code,
                                errorMessage: endorsementErrorStep.error_message,
                                errorStack: endorsementErrorStep.error_stack,
                            }
                            errorPeers.push( endorsementErrorStep.hostname )
                            let found = -1
                            for (let i = 0; i< this.endorsers.length; i++) {
                                if (this.endorsers[i].name == endorsementErrorStep.hostname) {
                                    found = i
                                    break
                                }
                            }
                            this.endorsers.splice(found, 1);
                            this.endorsers.push(errorPeer)
                            this.endorsementErrorSteps.push(endorsementErrorStep)
                        }
                    }

                    this.topologyDataReady = true;

                })
            },

            handleNodeMouseOver: function(event, nodeObject) {
                if (nodeObject._cssClass.includes('endorser')) {
                    for (let index = 0; index < this.endorsers.length; index++) {
                        if (this.endorsers[index].id == nodeObject.id) {
                            this.selectedNode = this.endorsers[index]
                            this.selectedNode.role = "Endorser"
                            break
                        }
                    }
                } else if (nodeObject._cssClass.includes('orderer')) {
                    for (let index = 0; index < this.orderers.length; index++) {
                        if (this.orderers[index].id == nodeObject.id) {
                            this.selectedNode = this.orderers[index]
                            this.selectedNode.role = "Orderer"
                            break
                        }
                    }                    
                }
                console.log("Selected node", this.selectedNode)
            },

            handleNodeMouseLeave: function(event, nodeObject) {
                this.selectedNode = null
            },

            getInvocationStageData: function() {
                this.$set(this.bcTransactionFlowChartSeries, 0, {data: []})
                Api.blockchainRequests.getInvocationStepsByRavenpodTxnId({},{ravenpod_txn_id: this.selectedBlockchainTxn.ravenpod_txn_id})
                .then((response1) => {
                    console.log("Invocation steps: ", response1.data)
                    let steps = response1.data
                    let results = []
                    for (let index=0; index < steps.length; index++) {
                        let step = steps[index]
                        let stageDesc, fillColor
                        switch (step.stage) {
                            case Constants.BLOCKCHAIN_STEP_STAGE_PROPOSAL:
                                stageDesc = 'PROPOSE'
                                fillColor = '#A1887F'
                                break;
                            case Constants.BLOCKCHAIN_STEP_STAGE_ENDORSEMENT:
                                stageDesc = 'ENDORSE'
                                fillColor = '#90A4AE'
                                break;
                            case Constants.BLOCKCHAIN_STEP_STAGE_ORDER:
                                stageDesc = 'ORDER'
                                fillColor = '#7986CB'
                                break;
                            case Constants.BLOCKCHAIN_STEP_STAGE_COMMIT:
                                stageDesc = 'COMMIT'
                                fillColor = '#BA68C8'
                                break;
                        }
                        results.push(
                            {
                                x: `${stageDesc} ${step.hostname}`,
                                y: [
                                    TimeHelper.toEpochTimeFromJSONDate(step.start_time),
                                    TimeHelper.toEpochTimeFromJSONDate(step.end_time)
                                ],
                                fillColor: fillColor
                            }
                        )
                    }
                    this.$set(this.bcTransactionFlowChartSeries, 0, {data: results})
                })
            },            

            getSlowestFunctions: function() {
                Api.blockchainRequests.getSlowestFunctions({},{ravenpod_txn_id: this.selectedBlockchainTxn.ravenpod_txn_id})
                .then((response1) => {
                    console.log("Slowest functions: ", response1.data)
                    this.slowestFunctionsSeries = []
                    const funcTimes = response1.data
                    funcTimes.forEach((funcTime) => {                        
                        this.slowestFunctionsSeries.push(
                            { module_name: funcTime.module_name, function_name: funcTime.function_name, name: `${funcTime.module_name}::${funcTime.function_name}`, data: [funcTime.avg_completion_time] }
                        )
                    })
                })                                
            },

            magicRender: function(tabNumber) {
                setTimeout(() => {this.playWithKey()}, 500)
            },

            playWithKey: function() {
                this.tabNumber += 1;
                console.log("New tab number = ", this.tabNumber);
            },            

            handleFullTraceClick: function() {
                this.traceViewDrawerStatus = 'expanded'
                this.highlightTraceModuleName = null
                this.highlightTraceFunctionName = null
                this.highlightTraceCompletionTime = 0
                this.getTrace()
            },

            getTrace: function() {
                this.traceRecords = [];                
                Api.blockchainRequests.getTrace({},{ravenpod_txn_id: this.selectedBlockchainTxn.ravenpod_txn_id})
                .then((response1) => {
                    console.log("Trace data", response1.data)
                    this.traceRecords = response1.data
                })                                
            },

            slowestFunctionsOptions: function() {
                let _this = this;
                return {
                    chart: {
                        type: 'bar',
                        stacked: true,
                        toolbar: { show: false },
                        events: {
                            click: function(event, chartContext, config) {
                                console.log( config )
                                _this.traceViewDrawerStatus = 'expanded'
                                _this.highlightTraceModuleName = _this.slowestFunctionsSeries[config.seriesIndex].module_name
                                _this.highlightTraceFunctionName = _this.slowestFunctionsSeries[config.seriesIndex].function_name
                                _this.highlightTraceCompletionTime = _this.slowestFunctionsSeries[config.seriesIndex].data[0]
                                _this.getTrace()
                            }
                        }           
                    },
                    dataLabels: {
                        enabled: true,
                        formatter: function(val, opt) {
                            return val + "ms"
                        },
                    },
                    colors: ColorHelper.getColors(),                  
                    plotOptions: {
                        bar: {
                            horizontal: true,
                            colors: {
                                ranges: [ { from: 100000, to: 200000, color: '#F44336' }],
                            },

                        },
                    },
                    stroke: {
                        width: 0,
                    },
                    xaxis: {
                        categories: [''],
                        labels: {
                            show: false,
                            formatter: function (val) {
                                return val + "ms"
                            }
                        }
                    },
                    yaxis: {
                        title: {
                            text: undefined
                        },
                    },
                    tooltip: {
                        y: {
                            formatter: function (val) {
                                return val + "ms"
                            }
                        }
                    },
                    fill: {
                        opacity: 1
                    },
                    legend: {
                        position: 'bottom',
                        horizontalAlign: 'left',
                        offsetX: 40
                    }
                }
            },                

        },

        data: function() {
            return {
                traceViewDrawerStatus: 'collapsed',
                tab: null,
                tabNumber: 0,
                topologyDataReady: false,
                endorsers: [],
                orderers: [],
                endorsementStatus: false,
                endorsementErrorSteps: [],
                selectedNode: null,
                traceRecords: [],
                highlightTraceModuleName: null,
                highlightTraceFunctionName: null,
                highlightTraceCompletionTime: 0,

                bcTransactionFlowChartSeries:  [{name: "Blockchain Request Flow", data:[]}],
                slowestFunctionsSeries: [],

                breathingColorOptions: {
                    colors: ColorHelper.getPurpleColors(),
                    interval: 2000,
                    transition: {
                        duration: 2000
                    }
                },            
                nodeOptions: {
                    height: 600,
                    offset: { x: 0, y: 0},      
                    force: 4000,
                    forces: {
                        X: 0.5,
                        Y: 1.0,
                        Center: false,
                    },                
                    nodeLabels: true,
                    linkWidth: 1,
                    canvas: false
                },
                bcTransactionFlowChartOptions: {
                    chart: {
                        type: 'rangeBar',
                        toolbar: { show: false }
                    },
                    plotOptions: {
                        bar: {
                            horizontal: true,
                            barHeight: '80%',
                            dataLabels: {
                                position: 'center',
                                hideOverflowingLabels: false
                            }
                        }
                    },
                    dataLabels: {
                        enabled: true,
                        style: {
                            colors: ['#000']
                        },
                        formatter: function(values, opt) {
                            return `${values[1] - values[0]}ms`
                        }
                    },                            
                    xaxis: {
                        type: 'datetime',
                        labels: {
                            formatter: function (value, timestamp) {
                                return TimeHelper.toHHMMSSFormat(value)
                            },                                 
                        }                            
                    },
                    yaxis: {
                        labels: {
                            align: 'left'
                        }
                    },
                    stroke: {
                        show: false
                    },
                    fill: {
                        type: 'solid',
                        opacity: 1,
                        colors: []
                    },
                    tooltip: {
                        enabled: true,
                        x: {
                            show: true,
                            formatter: function (value, timestamp) {
                                return TimeHelper.toShortFormat(value)
                            },                                 
                        },
                        y: {
                            title: {
                                formatter: (seriesName) => seriesName,
                            },                        
                        },
                        fixed: {
                            enabled: true,
                            position: 'topRight'
                        },                    
                    }
                },            

            }
        },    
    }
</script>

<style scoped lang="scss">

    .blockchain-invocation-detail-tabs {
        background-color: transparent !important;
    }

    .trace-event-row-odd {
        background-color: #90CAF9;
    }

    .trace-event-row-even {
        background-color: #BBDEFB;
    }

    .trace-event-cell {
        overflow-wrap: break-word;
        font-size: 12px;
    }

</style>